import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_URL, scrollToId } from "@/constants/brand";

const LINKS = [
  { label: "Requisiti", hash: "#requisiti", testid: "nav-link-requisiti" },
  { label: "Vantaggi", hash: "#vantaggi", testid: "nav-link-vantaggi" },
  { label: "Il Nostro Valore", hash: "#valore", testid: "nav-link-valore" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onLanding = location.pathname === "/";

  const goSection = (e, hash) => {
    setOpen(false);
    if (onLanding) {
      e.preventDefault();
      scrollToId(hash);
    }
  };

  return (
    <header
      data-testid="main-navbar"
      className="fixed top-0 inset-x-0 z-50 bg-obsidian/80 backdrop-blur-xl border-b border-brand/15"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 sm:h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 group">
          <img
            src={LOGO_URL}
            alt="Logo Io Amo La Mia Città"
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl object-cover ring-1 ring-brand/40 group-hover:ring-brand transition-[box-shadow] duration-300"
          />
          <span className="font-display font-extrabold tracking-tight text-sm sm:text-base text-white leading-tight">
            IO AMO LA <span className="text-brand">MIA CITTÀ</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {LINKS.map((l) => (
            <Link
              key={l.hash}
              to={`/${l.hash}`}
              onClick={(e) => goSection(e, l.hash)}
              data-testid={l.testid}
              className="text-sm font-semibold text-muted2 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/contatti"
            data-testid="nav-link-contatti"
            className="text-sm font-semibold text-muted2 hover:text-white transition-colors duration-300"
          >
            Contatti
          </Link>
          <Link
            to="/#candidati"
            onClick={(e) => goSection(e, "#candidati")}
            data-testid="nav-cta-candidati"
            className="inline-flex items-center gap-1.5 bg-brand hover:bg-brand-hot text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors duration-300 shadow-[0_10px_30px_-5px_rgba(232,67,147,0.35)]"
          >
            Candidati Ora <ArrowUpRight className="w-4 h-4" />
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-menu-btn"
          onClick={() => setOpen(!open)}
          className="md:hidden text-white p-2"
          aria-label="Apri menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-obsidian/95 backdrop-blur-xl border-b border-brand/15"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.hash}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i }}
                >
                  <Link
                    to={`/${l.hash}`}
                    onClick={(e) => goSection(e, l.hash)}
                    data-testid={`${l.testid}-mobile`}
                    className="font-display text-xl font-bold text-white"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
                <Link
                  to="/contatti"
                  onClick={() => setOpen(false)}
                  data-testid="nav-link-contatti-mobile"
                  className="font-display text-xl font-bold text-white"
                >
                  Contatti
                </Link>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}>
                <Link
                  to="/#candidati"
                  onClick={(e) => goSection(e, "#candidati")}
                  data-testid="nav-cta-candidati-mobile"
                  className="mt-2 inline-flex items-center gap-2 bg-brand hover:bg-brand-hot text-white font-bold px-6 py-3 rounded-full transition-colors duration-300"
                >
                  Candidati Ora <ArrowUpRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
